<template>
	<o-tour :ref="setup._tourOrderPipeline">
		<o-tour-step
			target="slider"
			size="md"
			title="Étapes de validation"
			hide-finish>
			<p>
				Cet onglet donne une vue globale des interactions et positionnement des candidats sur la commande.
			</p>
			<p>
				Vous pourrez <b>drag & drop</b> - <i>glisser déposer</i> - des candidats sur chacune d'entre elle.
			</p>
			<p>
				Chaque étape dispose d'une action de <b>multi-sélection</b> pour faciliter le déplacement
				de plusieurs candidats entre chaque étape.
				Cette action est représentée par la case à cocher.
			</p>
			<p>
				Il n'y a <b>pas d'ordre imposé</b>, ce qui signifie par exemple
				que vous pourrez proposer la mission au candidat avant de proposer le profil au client,
				ou encore valider un candidat sans même passer par les étapes intermédiaires.
			</p>

			<template #actions="{ next }">
				<o-button
					size="xs"
					outline
					@click="setup.returnToTourOrderDetails()">
					Précédent
				</o-button>
				<o-button
					size="xs"
					color="brand"
					@click="next">
					Suivant
				</o-button>
			</template>
		</o-tour-step>

		<o-tour-step
			:target="setup.scrollToId.bind(setup, 'step-0')"
			title="Préssenti"
			hide-finish>
			<p>
				L'étape <b>"Candidat pressenti"</b> est la première par laquelle il vous faudra passer !
			</p>
			<p>
				Lorsque vous cliquerez sur l'icône
				<o-icon
					style="margin-left:5px;margin-right:5px;"
					font-icon="icon-interface-add-circle"
					color="info"/>
				une fenêtre s'ouvrira pour <b>chercher le ou les candidats idéals</b>, suivant de nombreux filtres.
			</p>
			<p>
				Les candidats positionnés sur cette étape <b>ne seront pas contactés</b>, il s'agit plutôt de visualiser
				un panel de candidatures éventuelles qui vous serviront pour les prochaines étapes.
			</p>
		</o-tour-step>

		<o-tour-step
			:target="setup.scrollToId.bind(setup, 'step-1')"
			title="Proposé au candidat"
			size="lg"
			hide-finish>
			<p>
				Proposez la mission à l'un des candidats en le glissant sur cette étape.
			</p>
			<p>
				Ca faisant, il recevra automatiquement un mail - <i>et une notification sur son application mobile s'il l'a téléchargé</i> -
				avec <b>le détail de l'offre de mission</b>.
			</p>
			<o-alert color="info">
				Le descriptif de la mission qu'il reçoit correspond à la description du poste saisi dans la dernière étape de création de la commande.
				<br>
				Vous pouvez <b>gagner du temps</b> en préparant à l'avance les descriptifs de poste correspondants à une qualification bien particulière !
			</o-alert>
			<p>
				Le candidat, à l'identique du client, pourra se <b>positionner</b> depuis son espace Armado.
			</p>
			<p class="flex ai-c g-xs">
				Tant qu'il n'a pas répondu, c'est cette icône qui apparait
				<shared-state-icon
					small
					:state="'warning'"/>
			</p>
			<div>
				Sa réponse, qui peut également comporter un <b>commentaire</b>, sera visible par la couleur que prendra l'icone
				<br>
				<div class="flex ai-c g-xs">
					<shared-state-icon
						small
						:state="'complete'"/>en cas de <span
							style="font-weight: bold;"
							class="text--success">
							validation de l'offre
						</span>
				</div>
				<div class="flex ai-c g-xs">
					<shared-state-icon
						small
						:state="'danger'"/> en cas de <span
							style="font-weight: bold;"
							class="text--danger">
							refus de l'offre
						</span>.
				</div>
			</div>
		</o-tour-step>

		<o-tour-step
			:target="setup.scrollToId.bind(setup, 'step-2')"
			size="lg"
			title="Proposé au client"
			hide-finish>
			<div>
				Lorsque vous selectionnerez un candidat, vous pourrez le déposer sur cette étape afin
				de le <b>proposer au client</b>.
				<br>
				<div class="flex ai-c g-xs">
					L'icône suivante apparaitra
					<shared-state-icon
						small
						:state="'warning'"/>
				</div>
			</div>
			<p>
				Il sera averti par mail de cette proposition et enjoint à <b>se positionner sur la pertinence</b> du candidat.
				<br>
				Vous serez également <b>prévenu de sa réponse par mail</b>.
				<br>
				L'icône sera par ailleurs colorée suivant l'avis donné par le client :
			</p>
			<div>
				<div class="flex ai-c g-xs">
					<shared-state-icon
						small
						:state="'complete'"/> en cas de <span
							style="font-weight: bold;"
							class="text--success">
							validation du candidat
						</span>
				</div>
				<div class="flex ai-c g-xs">
					<shared-state-icon
						small
						:state="'danger'"/> en cas de <span
							style="font-weight: bold;"
							class="text--danger">
							refus du candidat
						</span>.
				</div>
			</div>
			<p>
				En survolant l'icône, vous verrez également le <b>commentaire</b> associé à son choix s'il en a saisit un.
			</p>
			<p>
				Le profil est aujourd'hui systématiquement <b>anonymisé</b> et sera alors visualisable sur l'espace Armado de l'utilisateur
				EU, dans le <b>détail de sa commande</b>.
			</p>
			<o-alert
				v-if="!setup.warnClient"
				color="warning">
				Cette action n'est <b>pas disponible</b> sur cette commande car elle n'a pas été partagée au client !
				<br>
				Vous ne pourrez donc pas glisser de candidat sur cette étape.
				<br>
				Si vous changez d'avis, l'icône
				<o-icon
					v-tooltip="`Partager avec l'EU`"
					style="margin-left:8px; margin-right:5px; font-size:18px;"
					font-icon="icon-upload-box-2-2"/>
				située en haut à droite de la page est prévue à cet effet.
			</o-alert>

			<template #actions="{ next, previous }">
				<o-button
					v-if="!setup.warnClient"
					size="xs"
					outline
					color="info"
					@click="setup.updateWarnClient()">
					Partager la commande au client
				</o-button>
				<o-button
					size="xs"
					outline
					@click="previous">
					Précédent
				</o-button>
				<o-button
					size="xs"
					color="brand"
					@click="next">
					Suivant
				</o-button>
			</template>
		</o-tour-step>



		<o-tour-step
			:target="setup.scrollToId.bind(setup, 'step-3')"
			title="Validé"
			size="lg">
			<p>
				Avant de valider définitivement la commande, et si vous avez partagé la mission ou des candidats
				lors des précédentes étapes, n'oubliez pas de glisser les <b>profils validés</b>
				ici !
			</p>
			<p>
				Ainsi, lors de la validation finale :
				<ul>
					<li>
						le client sera informé de la validation définitive des profils mis à disposition.
						<br>
						Profils qu'il pourra consulter depuis son espace Armado
					</li>
					<li>
						les candidats seront informés que la mission est pour eux !
					</li>
				</ul>
			</p>
			<p>
				Si vous utilisez le module de commande pour <b>interagir avec vos tiers</b>, il est donc essentiel d'aller au bout de la démarche
				et de les glisser dans cette étape afin d'activer les <b>mécaniques automatiques d'alertes</b> proposées par Armado !
			</p>
			<p>
				La jauge colorée vous donnera un aperçu du pourcentage de <b>complétion</b> de la commande.
			</p>
			<template #actions="{ previous }">
				<o-button
					size="xs"
					outline
					@click="previous">
					Précédent
				</o-button>
				<o-button
					size="xs"
					outline
					@click="setup.returnToTourOrderDetails()">
					Revenir au tutoriel
				</o-button>
			</template>
		</o-tour-step>
	</o-tour>
</template>

<script setup lang="ts">
import TourOrderPipelineSetupService from '@/setup/tour/TourOrderPipelineSetupService';
const setup = new TourOrderPipelineSetupService();
</script>
